import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import { getAircraftOperationRecords, upsertAircraftOperationRecord } from "src/api/aircraft";
import type { UserAircraftOperationHistoryEntry } from "src/shared/api-types/aircraft";
import type { AssignedLimitation, AssignedLimitationInput } from "src/shared/api-types/limitations";
import { captureHandledException } from "src/services/sentry";
import { getTranslatedApiError } from "src/utils/apiError";

import type { AircraftOperationState } from "../types";

type OperationKey = `${number}:${number}`;

function makeKey(typeId: number, operationId: number): OperationKey {
  return `${typeId}:${operationId}`;
}

interface UseAircraftOperationsReturn {
  states: Record<OperationKey, AircraftOperationState>;
  operationNames: Record<number, string>;
  isLoading: boolean;
  update: (
    typeId: number,
    operationId: number,
    enabled: boolean,
    underSupervision: boolean,
    limitations?: AssignedLimitationInput[],
    comment?: string,
  ) => Promise<UserAircraftOperationHistoryEntry | null>;
  reload: () => Promise<void>;
}

export function useAircraftOperations(crewId: number, userId: number): UseAircraftOperationsReturn {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [states, setStates] = useState<Record<OperationKey, AircraftOperationState>>({});
  const [operationNames, setOperationNames] = useState<Record<number, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  const statesRef = useRef(states);

  useEffect(() => {
    statesRef.current = states;
  }, [states]);

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      const records = await getAircraftOperationRecords({ crewId, userId });
      const next: Record<OperationKey, AircraftOperationState> = {};
      const names: Record<number, string> = {};
      for (const r of records) {
        const key = makeKey(r.typeId, r.operationId);
        next[key] = {
          typeId: r.typeId,
          operationId: r.operationId,
          enabled: r.enabled,
          underSupervision: r.underSupervision,
          limitations: r.limitations ?? [],
          saving: false,
        };
        names[r.operationId] = r.operationName;
      }
      setStates(next);
      setOperationNames(names);
    } catch (err) {
      captureHandledException(err, { feature: "aircraftOperations", action: "load", crewId, userId });
      enqueueSnackbar(t("aircraft_management.load_operations_failed"), { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  }, [crewId, userId, t, enqueueSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  const update = useCallback(
    async (
      typeId: number,
      operationId: number,
      enabled: boolean,
      underSupervision: boolean,
      limitations?: AssignedLimitationInput[],
      comment?: string,
    ): Promise<UserAircraftOperationHistoryEntry | null> => {
      const key = makeKey(typeId, operationId);
      const currentLimitations = toLimitationInputs(statesRef.current[key]?.limitations ?? []);
      const nextLimitations = enabled ? (limitations ?? currentLimitations) : [];

      setStates((prev) => ({
        ...prev,
        [key]: {
          typeId,
          operationId,
          enabled,
          underSupervision,
          limitations: prev[key]?.limitations ?? [],
          saving: true,
        },
      }));

      try {
        const historyEntry = await upsertAircraftOperationRecord(
          { userId, crewId, typeId, operationId },
          { enabled, underSupervision, limitations: nextLimitations, comment },
        );
        setStates((prev) => ({
          ...prev,
          [key]: {
            typeId,
            operationId,
            enabled,
            underSupervision,
            limitations: historyEntry.limitations as AssignedLimitation[],
            saving: false,
          },
        }));
        return historyEntry;
      } catch (err) {
        captureHandledException(err, {
          feature: "aircraftOperations",
          action: "update",
          crewId,
          userId,
          typeId,
          operationId,
        });
        enqueueSnackbar(await getTranslatedApiError(err, t, "aircraft_management.save_operation_failed"), {
          variant: "error",
        });
        setStates((prev) => ({
          ...prev,
          [key]: {
            ...(prev[key] ?? { typeId, operationId, enabled: false, underSupervision: false, limitations: [] }),
            saving: false,
          },
        }));
        return null;
      }
    },
    [userId, crewId, t, enqueueSnackbar],
  );

  return { states, operationNames, isLoading, update, reload: load };
}

export function getOperationState(
  states: Record<OperationKey, AircraftOperationState>,
  typeId: number,
  operationId: number,
): AircraftOperationState {
  return (
    states[makeKey(typeId, operationId)] ?? {
      typeId,
      operationId,
      enabled: false,
      underSupervision: false,
      limitations: [],
      saving: false,
    }
  );
}

export function toLimitationInputs(limitations: AssignedLimitation[]): AssignedLimitationInput[] {
  return limitations.map((limitation) => ({
    definitionId: limitation.definitionId,
    optionId: limitation.optionId,
  }));
}
